import { activateDependentById } from '@brands/services/identity/activateDependentById';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { listDependents } from '@brands/services/identity/listDependents';
import { Dependent } from '@brands/services/identity/types/Dependent';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { setDependents } from '@brands/store/slices/dependentsSlice';
import { checkProfileForm } from '@brands/Utils/checkExternalForms';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IconType } from 'react-icons';
import { BsToggleOn } from 'react-icons/bs';
import { FiEdit } from 'react-icons/fi';
import { useParams } from 'react-router-dom';

import { Loading } from '../../../../../Components/LoadingSpinner/Loading';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/useReduxHook';
import { UserRoleName } from '../../../../../services/identity/types/UserProfile';
import styles from '../../styles.module.scss';
import addFilled from './AddFilled.svg';

type ReadOnlyProfileProps = {
  onEditDependent: (id: number) => void;
  addDependent: () => void;
  reviewProfile: boolean;
};

const ReadOnlyDependent = ({
  onEditDependent,
  addDependent,
  reviewProfile,
}: ReadOnlyProfileProps): JSX.Element => {
  const { userId } = useParams();
  const { userInfo } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const { selectedPatient } = useAppSelector(selectPatient);
  const [dependentList, setDependentList] = useState<Dependent[]>([]);
  const [deactivatedDependentList, setDeactivatedDependentList] = useState<Dependent[]>([]);
  const [deactivatedDependentListVisible, setDeactivatedDependentListVisible] = useState(false);

  const setAllDependents = async (): Promise<void> => {
    const listOfDependents = await listDependents(!reviewProfile ? userInfo.id : userId, { include_deleted: true });
    if (listOfDependents.length > 0) {
      setDependentList(listOfDependents);
      dispatch(
        setDependents(
          listOfDependents
            .slice() // Create a shallow copy to avoid mutating the original array
            .sort((a, b) => b.id - a.id) // Sort in descending order based on the 'id' property
            .map((dependent) => ({
              value: dependent.id.toString(),
              label: dependent.full_name,
              status: dependent.status,
            })),
        ),
      );
    } else {
      setDependentList([]);
      dispatch(setDependents([]));
    }
    if (listOfDependents.filter((dependent) => dependent?.status === 'Deactivated').length > 0) {
      setDeactivatedDependentList(listOfDependents);
    } else {
      setDeactivatedDependentList([]);
    }
  };

  const onReactivateDependent = async (id: number): Promise<void> => {
    setLoading(true);
    try {
      await activateDependentById(
        userInfo.role.name === UserRoleName.Admin ||
          userInfo.role.name === UserRoleName.SuperAdmin ||
          userInfo.role.name === UserRoleName.OrganizationAdmin
          ? selectedPatient.id
          : userInfo.id,
        id,
      );
      await setAllDependents();
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await setAllDependents();
      } catch (error) {
        // DO-3953: without this catch, any 500 from `listDependents`
        // (e.g. the User-schema-lazy-load regression in DO-3794) leaves
        // the loading flag stuck at `true` and the page shows three
        // cycling dots forever. Preserve the original successful-path
        // behavior; on failure, just unstick the spinner so the user
        // can still use the rest of the screen.
        displayErrorDetails(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Admins manage patients on behalf of them — they should be able to
  // add a dependent regardless of whether the patient's own profile is
  // complete. Non-admin (patient self-serve) paths still need the
  // patient's profile to be filled out before a dependent can be added,
  // since the dependent inherits contact / address fields from the
  // guardian.
  const isAdminViewing =
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin;
  const canAddDependent = isAdminViewing || checkProfileForm(userInfo, null);

  const renderActionButton = (): JSX.Element => {
    return (
      <Button
        className={`fs-unmask ${styles.addBtn}`}
        type="button"
        variant="contained"
        data-testid="profile-add-dependent"
        onClick={canAddDependent ? addDependent : undefined}
        disabled={!canAddDependent}
      >
        <img src={addFilled} />
        Add dependent
      </Button>
    );
    return <div />;
  };

  const getInitials = (firstName?: string, lastName?: string): string => {
    const firstInitial = firstName?.charAt(0) || '';
    const lastInitial = lastName?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const renderDependentList = (
    list: Dependent[],
    status: string,
    actionText: string,
    actionIcon: IconType,
    actionHandler: (id: number) => void,
  ): JSX.Element => {
    if (list.length !== 0) {
      return (
        <ul className={styles.dependentsList}>
          {list
            .filter((dependents) => dependents.status === status)
            .map((dependent) => (
              <li key={dependent?.id} className={styles.dependentItem}>
                {dependent?.picture_media_id ? (
                  <div className={styles.dependentImage}>
                    <img className="fs-exclude" src={getUserAvatar(dependent.id)} alt="profile" />
                  </div>
                ) : (
                  <div className={styles.initialsCircle}>
                    {getInitials(dependent?.basic_info?.first_name ?? '', dependent?.basic_info?.last_name ?? '')}
                  </div>
                )}
                <div>
                  <div className={`fs-exclude ${styles.dependentName}`}>{dependent.full_name || '-'}</div>
                  <Button
                    variant="text"
                    className={styles.editBtn}
                    type="button"
                    onClick={() => actionHandler(dependent.id)}
                    data-testid="profile-edit-dependent"
                  >
                    {actionText}
                  </Button>
                </div>
              </li>
            ))}
        </ul>
      );
    }
    return <div className={styles.noDependent}>No Dependents</div>;
  };

  const renderTitle = (): JSX.Element => (
    <>
      <div className={`${styles.titleContainer} ${styles.mobileFlexRevCol} ${styles.mobileAddDependent}`}>
        <span className={`fs-unmask ${styles.Dependentstitle}`}>Dependents</span>
        {renderActionButton()}
      </div>
      {!canAddDependent && <span>You need to complete your profile to add a dependent.</span>}
    </>
  );

  const renderDependentSection = (): JSX.Element => {
    if (loading) {
      return <Loading dotted />;
    }

    if (dependentList.length !== 0) {
      return renderDependentList(dependentList, 'Active', 'Edit profile', FiEdit, onEditDependent);
    }

    return <div className={styles.noDependent}>No Dependents</div>;
  };

  const renderDeactivatedDependents = (): JSX.Element => {
    if (loading) {
      return <Loading dotted />;
    }

    if (deactivatedDependentList.length !== 0) {
      return (
        <>
          <Button
            variant="text"
            className={styles.readOnlyProfileShowDeactivatedDependentsButton}
            onClick={() => setDeactivatedDependentListVisible(!deactivatedDependentListVisible)}
          >
            {deactivatedDependentListVisible ? 'Hide Deactivated Dependents' : 'View deactivated dependents'}
          </Button>
          {deactivatedDependentListVisible &&
            renderDependentList(
              deactivatedDependentList,
              'Deactivated',
              'Reactivate',
              BsToggleOn,
              onReactivateDependent,
            )}
        </>
      );
    }

    return <div className={styles.noDependent} />;
  };

  return (
    <div className={styles.readOnlyProfileContainer}>
      {renderTitle()}
      {renderDependentSection()}
      <div className={styles.readOnlyProfileDeactivatedDependents}>{renderDeactivatedDependents()}</div>
    </div>
  );
};

export default ReadOnlyDependent;
